import { assessmentData } from '../data/questions';

export const calculateResults = (answers) => {
  let totalScore = 0;
  let totalMax = 0;
  const allSections = [];

  assessmentData.forEach(section => {
    let sectionScore = 0;
    const sectionMax = section.questions.length * 2;

    section.questions.forEach(q => {
      const rawVal = answers[q.id];
      const val = (rawVal && typeof rawVal === 'object') ? rawVal.value : (rawVal || 0);
      sectionScore += val;
    });

    totalScore += sectionScore;
    totalMax += sectionMax;
    allSections.push({
      id: section.id,
      title: section.title,
      score: sectionScore,
      max: sectionMax,
      percentage: Math.round((sectionScore / sectionMax) * 100)
    });
  });

  const finalPercentage = Math.round((totalScore / totalMax) * 100);
  
  let riskLevel = "";
  let riskColor = "";
  if (finalPercentage <= 40) {
    riskLevel = "Riesgo Alto — Acción Inmediata Requerida";
    riskColor = "text-red-500";
  } else if (finalPercentage <= 65) {
    riskLevel = "Riesgo Medio — Seguridad Básica en Progreso";
    riskColor = "text-orange-500";
  } else if (finalPercentage <= 85) {
    riskLevel = "Riesgo Bajo — Buen Nivel de Preparación";
    riskColor = "text-yellow-500";
  } else {
    riskLevel = "Nivel Óptimo — Red Bien Organizada";
    riskColor = "text-emerald-500";
  }

  // Críticas: Los 3 más bajos (Ascendente)
  const criticalAreas = [...allSections]
    .sort((a, b) => a.percentage - b.percentage)
    .slice(0, 3);

  // Fuertes: Los 3 más altos (Descendente)
  const strongAreas = [...allSections]
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 3);

  return {
    totalScore,
    totalMax,
    percentage: finalPercentage,
    riskLevel,
    riskColor,
    sectionScores: [...allSections].sort((a, b) => a.percentage - b.percentage),
    criticalAreas,
    strongAreas
  };
};

export const getPriorityLabel = (pct) => {
  if (pct < 40) return 'ALTA';
  if (pct < 60) return 'MEDIA';
  if (pct < 80) return 'BAJA';
  return 'MANTENER / FORTALEZA';
};

export const getDetailedRecommendations = (sectionId) => {
  const recs = {
    "org": [
      "Asignar formalmente a un responsable de las decisiones de seguridad.",
      "Documentar el proceso de aprobación para nuevos usuarios y accesos.",
      "Establecer una revisión trimestral de riesgos con la gerencia."
    ],
    "inv": [
      "Crear un inventario actualizado de equipos, laptops y servidores.",
      "Listar todas las aplicaciones en la nube (SaaS) usadas por el equipo.",
      "Identificar y clasificar la información más sensible del negocio."
    ],
    "acc": [
      "Activar Autenticación Multifactor (MFA) en correos y sistemas críticos.",
      "Realizar una purga inmediata de cuentas de exempleados.",
      "Limitar los permisos de administrador solo a tareas esenciales."
    ],
    "red": [
      "Revisar la configuración del firewall y cerrar puertos innecesarios.",
      "Separar la red Wi-Fi de invitados de la red interna de trabajo.",
      "Implementar actualizaciones automáticas en todos los equipos."
    ],
    "det": [
      "Configurar alertas de inicio de sesión inusual en sistemas clave.",
      "Designar a alguien para revisar reportes de antivirus semanalmente.",
      "Establecer un canal para reportar comportamientos extraños en equipos."
    ],
    "cap": [
      "Realizar una capacitación básica sobre phishing y correos sospechosos.",
      "Crear un canal simple para que el equipo reporte actividad dudosa.",
      "Fomentar una política de reporte sin culpas para errores de seguridad."
    ],
    "res": [
      "Crear una lista de contactos de emergencia para incidentes técnicos.",
      "Definir pasos básicos para aislar un equipo ante sospecha de virus.",
      "Documentar incidentes previos para aprender de las debilidades."
    ],
    "rec": [
      "Asegurar respaldos automáticos y que uno esté fuera de la oficina.",
      "Realizar una prueba de restauración de archivos mensualmente.",
      "Tener un plan de contingencia si la red falla por varias horas."
    ],
    "evi": [
      "Crear una carpeta centralizada para organizar evidencia de seguridad.",
      "Guardar capturas o reportes que demuestren que los controles funcionan.",
      "Mantener un registro de cambios importantes en la infraestructura."
    ],
    "pro": [
      "Solicitar al proveedor de TI reportes regulares de seguridad.",
      "Definir tiempos de respuesta ante incidentes fuera de horario.",
      "Revisar y limitar el acceso permanente que el proveedor tiene a tus datos."
    ]
  };
  return recs[sectionId] || ["Revisar esta área para mejorar los controles actuales."];
};

const formatSectionTitle = (title) => {
  // Asegura que TI se mantenga en mayúsculas
  return title.replace(/ti/gi, 'TI');
};

export const generateExecutiveSummary = (results) => {
  if (!results) return "Información no disponible.";
  
  const strongNames = (results.strongAreas || [])
    .filter(s => s.percentage >= 60)
    .map(s => formatSectionTitle(s.title));
    
  const weakNames = (results.criticalAreas || [])
    .filter(s => s.percentage < 60)
    .map(s => formatSectionTitle(s.title));

  let summary = `Tu empresa muestra `;
  
  if (strongNames.length > 0) {
    summary += `avances significativos en ${strongNames.join(', ')}. `;
  } else {
    summary += `la necesidad de establecer controles básicos de seguridad iniciales para proteger la operación. `;
  }

  if (weakNames.length > 0) {
    summary += `Sin embargo, las áreas que requieren atención inmediata debido a su vulnerabilidad son ${weakNames.join(', ')}. Estas dimensiones deben priorizarse en el corto plazo para reducir riesgos operativos y mejorar la postura de ciberseguridad defensiva del negocio.`;
  } else {
    summary += `Se recomienda mantener la vigilancia constante y actualizar periódicamente los controles existentes para asegurar la continuidad del nivel de protección alcanzado.`;
  }

  return summary;
};